import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { THEME_STORAGE_KEY, ThemeContext, type Theme } from '@/shared/lib/theme'

function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'system'
  } catch {
    return 'system'
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme)

  useEffect(() => {
    const root = document.documentElement

    if (theme !== 'system') {
      root.dataset.theme = theme
      return
    }

    /*
     * system тоже разворачивается в конкретный атрибут, а не снимается: тогда в tokens.css
     * хватает одного тёмного блока вместо двух, и новый токен пишется один раз, а не трижды.
     */
    const query = window.matchMedia('(prefers-color-scheme: dark)')
    const apply = () => {
      root.dataset.theme = query.matches ? 'dark' : 'light'
    }

    apply()
    query.addEventListener('change', apply)
    return () => query.removeEventListener('change', apply)
  }, [theme])

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
    try {
      if (next === 'system') localStorage.removeItem(THEME_STORAGE_KEY)
      else localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      /* приватный режим — тема не переживёт перезагрузку */
    }
  }, [])

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme])

  return <ThemeContext value={value}>{children}</ThemeContext>
}
